/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useListContext, useTranslate } from "ra-core";
import matches from "lodash/matches";
import pickBy from "lodash/pickBy";
import { CircleX } from "lucide-react";

/**
 * A button that toggles a specific filter value on/off.
 *
 * Renders a button that applies or removes a filter when clicked. Shows a close icon when the
 * filter is active. Useful for quick filter presets like status or category.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/togglefilterbutton/ ToggleFilterButton documentation}
 *
 * @example
 * import { ToggleFilterButton } from '@/components/admin';
 *
 * const PostFilters = () => (
 *   <div className="flex flex-row gap-2">
 *     <ToggleFilterButton label="Published" value={{ status: 'published' }} />
 *     <ToggleFilterButton label="Draft" value={{ status: 'draft' }} />
 *   </div>
 * );
 */
export const ToggleFilterButton = ({
  label,
  size = "sm",
  value,
  className,
}: {
  label: React.ReactElement | string;
  value: any;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon" | null | undefined;
}) => {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const isSelected = getIsSelected(value, filterValues);
  const handleClick = () => setFilters(toggleFilter(value, filterValues));
  return (
    <Button
      variant={isSelected ? "secondary" : "ghost"}
      onClick={handleClick}
      className={cn(
        "cursor-pointer",
        "flex flex-row items-center justify-between gap-2 px-2.5 w-full",
        className,
      )}
      size={size}
    >
      {typeof label === "string" ? translate(label, { _: label }) : label}
      {isSelected && <CircleX className="opacity-50" />}
    </Button>
  );
};

const normalizeFilterValues = (obj: Record<string, unknown>) => {
  const next: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === "undefined") continue;
    // URL/query often rehydrates numeric ids as strings — coerce so "Me" stays selected.
    if (
      (key === "sales_id" || key === "id" || key.endsWith("_id")) &&
      val !== null &&
      val !== "" &&
      !Number.isNaN(Number(val))
    ) {
      next[key] = Number(val);
    } else {
      next[key] = val;
    }
  }
  return next;
};

const toggleFilter = (value: any, filters: any) => {
  const cleanedValue = normalizeFilterValues(
    pickBy(value, (val) => typeof val !== "undefined"),
  );
  const cleanedFilters = normalizeFilterValues(filters || {});
  const isSelected = matches(cleanedValue)(cleanedFilters);

  if (isSelected) {
    const keysToRemove = Object.keys(value);
    return Object.keys(filters || {}).reduce(
      (acc, key) =>
        keysToRemove.includes(key) ? acc : { ...acc, [key]: filters[key] },
      {},
    );
  }

  return { ...filters, ...value };
};

const getIsSelected = (value: any, filters: any) =>
  matches(
    normalizeFilterValues(pickBy(value, (val) => typeof val !== "undefined")),
  )(normalizeFilterValues(filters || {}));
